import type { ReactNode } from "react";
import Link from "next/link";
import { CHECK_AVAILABILITY_LABEL } from "@shared/products/listing-copy";
import { cn } from "@/lib/utils";
import type { ProductCardDisplay } from "./product-card-display";
import { CardArt, CardBody, CardFacts, CardPrice, CardSpec } from "./product-card-parts";

const CARD_CLASS =
  "group group/card relative flex h-full min-w-0 flex-col rounded-xl";

const PHONE_ROW_CARD_CLASS =
  "max-md:grid max-md:grid-cols-[6.5rem_minmax(0,1fr)_auto] max-md:items-start max-md:gap-x-4 max-md:py-4";

function QuoteLink({ display, quoteHref }: { display: ProductCardDisplay; quoteHref?: string }) {
  if (display.inStock || display.isFreePickup || !quoteHref) return null;
  return (
    <Link
      prefetch={false}
      href={quoteHref}
      aria-label={`${CHECK_AVAILABILITY_LABEL} — ${display.name || "this listing"}`}
      className="relative z-20 mt-1 inline-flex min-h-11 items-center self-start text-[0.9375rem] font-medium text-link underline underline-offset-4 hover:text-link-hover"
    >
      {CHECK_AVAILABILITY_LABEL}
    </Link>
  );
}

function CardMedia({
  display,
  priority,
  sizes,
  saveSlot,
}: {
  display: ProductCardDisplay;
  priority: boolean;
  sizes?: string;
  saveSlot?: ReactNode;
}) {
  return (
    <div className="relative group-data-[phone-row]/card:max-md:contents">
      <CardArt
        imageUrl={display.imageUrl}
        title={display.name}
        productTypeSlug={display.productTypeSlug}
        priority={priority}
        sizes={sizes}
      />
      {saveSlot ? (
        <div className="absolute right-2.5 top-2.5 z-20 max-md:right-2 max-md:top-2 group-data-[phone-row]/card:max-md:static group-data-[phone-row]/card:max-md:col-start-3 group-data-[phone-row]/card:max-md:row-start-1">
          {saveSlot}
        </div>
      ) : null}
    </div>
  );
}

export function ProductCardView({
  display,
  priority = false,
  sizes,
  saveSlot,
  quoteHref,
  phoneRow = false,
}: {
  display: ProductCardDisplay;
  priority?: boolean;
  sizes?: string;
  saveSlot?: ReactNode;
  quoteHref?: string;
  phoneRow?: boolean;
}) {
  return (
    <article className={cn(CARD_CLASS, phoneRow && PHONE_ROW_CARD_CLASS)} data-phone-row={phoneRow ? "" : undefined}>
      <CardMedia display={display} priority={priority} sizes={sizes} saveSlot={saveSlot} />
      <CardBody>
        <CardSpec productName={display.name} grade={display.grade} dimensions={display.dimensions} href={display.href} />
        <CardPrice unitPrice={display.unitPrice} freeLabel={display.freeLabel} />
        <CardFacts
          location={display.location}
          quantityAvailable={display.quantityAvailable}
          inStock={display.inStock}
          pickupOnly={display.pickupOnly}
        />
        <QuoteLink display={display} quoteHref={quoteHref} />
      </CardBody>
    </article>
  );
}
